import { setError, setLoaded, setLoading, withCallState, withLocalStorage, withStorageSync } from '@angular-architects/ngrx-toolkit';
import { effect, inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { AuthToken } from '@libs/shared/models';
import { patchState, signalStore, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { firstValueFrom, from, Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { UserAuthHandler } from '../auth.model';

/* Auth Token */
interface AuthTokenStateModel {
  token: AuthToken | undefined;
}

const initialAuthTokenState: AuthTokenStateModel = {
  token: undefined,
};

@Injectable({ providedIn: 'root' })
export class AuthTokenState extends signalStore(
  withState(initialAuthTokenState),
  withCallState(),
  withStorageSync({
    key: 'cepAuthToken',
    select: ({ token }) => ({ token }),
  }, withLocalStorage()),
  withProps(() => ({
    authHandler: inject(UserAuthHandler),
  })),
  withMethods(store => ({
    updateToken: async (token?: AuthToken): Promise<void> => {
      return new Promise<void>((resolve) => {
        patchState(store, { token }, setLoaded());
        setTimeout(resolve);
      });
    },
    _handleError: (error: unknown): Promise<undefined> => {
      patchState(store, { token: undefined }, setLoaded(), setError(error));
      console.error(error);

      return Promise.reject(error instanceof Error ? error : new Error(String(error)));
    },
  })),
  withMethods(store => ({
    logout: async (): Promise<void> => {
      patchState(store, { token: undefined }, setLoading());

      return firstValueFrom(store.authHandler.logout())
        .then(() => {
          patchState(store, setLoaded());
        }).catch(error => {
          return store._handleError(error);
        });
    },
  })),
  withMethods(store => ({
    reset: (): void => {
      patchState(store, { token: undefined }, setLoaded());
    },
    agentSetUser: async (userId: string): Promise<void> => {
      return new Promise<void>((resolve, reject) => {
        const currentToken: AuthToken | undefined = store.token();

        if (!currentToken) {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
          reject();

          return;
        }

        patchState(store, {
          token: {
            ...currentToken,
            userId: userId,
          },
        }, setLoaded());

        setTimeout(resolve);
      });
    },
    login: async (newToken?: AuthToken): Promise<void> => {
      patchState(store, setLoading());

      if (newToken) {
        return store.updateToken(newToken);
      }

      return firstValueFrom(store.authHandler.login())
        .then(token => {
          return store.updateToken(token);
        })
        .catch(error => {
          return store._handleError(error);
        });
    },
    register: async (): Promise<void> => {
      patchState(store, setLoading());

      return firstValueFrom(store.authHandler.register())
        .then(token => {
          return store.updateToken(token);
        })
        .catch(error => {
          return store._handleError(error);
        });
    },
    refresh: async (): Promise<AuthToken | undefined> => {
      patchState(store, setLoading());

      return firstValueFrom(store.authHandler.refresh())
        .then(token => {
          patchState(store, { token }, setLoaded());

          return token;
        })
        .catch(async error => {
          await store.logout();

          return store._handleError(error);
        });
    },
  })),
  withProps(store => ({
    _actualToken$: toObservable(store.token).pipe(
      switchMap(token => {
        if (!token || new Date() < new Date(token.expires)) {
          return of(token);
        }

        return from(store.refresh());
      }),
    ),
  })),
  withMethods(store => ({
    readToken: (): Observable<AuthToken | undefined> => {
      return store._actualToken$;
    },
  })),
  withHooks(store => ({
    onInit(): void {
      effect(() => {
        //eslint-disable-next-line no-console
        console.info(`User is ${store.token() ? 'authenticated' : 'anonymous'}`);
      });
    },
  })),
) {
}
